import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Upload, X, Save, Edit3, Image as ImageIcon } from "lucide-react";
import { api, formatApiError, formatINR, useSEO } from "@/lib/api";

const field = "w-full bg-[#F5F5F5] rounded-2xl px-5 py-3.5 text-sm outline-none focus:ring-1 ring-black transition";
const label = "block text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2";

const emptyForm = {
  slug: "",
  name: "",
  tagline: "Signature Collection",
  price: 1899,
  compare_at_price: 5999,
  category: "Sunglasses",
  gender: "unisex",
  collections: ["new-arrivals"],
  description: "",
  images: [],
  colors: [],
  sizes: ["One Size"],
  features: [],
  specs: {},
  in_the_box: ["VYRON Luxury Sunglasses", "Premium Hard Case", "Microfiber Cleaning Cloth"],
  rating: 4.9,
  review_count: 0,
  best_seller: true,
  new_arrival: true,
  featured: true,
  span: 1,
  badge: "New Arrival",
  sku: "",
  inventory: 50,
  shape: "square",
  tags: [],
  active: true,
};

const COLLECTION_OPTIONS = [
  { v: "new-arrivals", l: "New Arrivals" },
  { v: "signature-collection", l: "Signature Collection" },
];

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function AdminProducts() {
  useSEO("Admin — Products");
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // slug or "new"
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing("new");
    setForm({ ...emptyForm, slug: "" });
  };

  const openEdit = (p) => {
    setEditing(p.slug);
    setForm({ ...emptyForm, ...p });
  };

  const cancel = () => { setEditing(null); setForm(emptyForm); };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setListField = (k, v) => setField(k, v.split("\n").map((x) => x.trim()).filter(Boolean));
  const setSpecsField = (v) => {
    const specs = {};
    v.split("\n").forEach((ln) => {
      const [key, ...rest] = ln.split(":");
      if (key && rest.length) specs[key.trim()] = rest.join(":").trim();
    });
    setField("specs", specs);
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("images", [...form.images, data.url]);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const onFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(uploadFile);
    e.target.value = "";
  };

  const removeImage = (idx) => setField("images", form.images.filter((_, i) => i !== idx));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Product name is required.");
    if (!form.price || form.price < 1) return toast.error("Valid price is required.");
    if (!form.images.length) return toast.error("Please upload at least one product image.");
    const payload = { ...form };
    if (editing === "new" && !payload.slug) payload.slug = slugify(payload.name);
    if (!payload.slug) return toast.error("Slug is required.");
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      } else {
        await api.put(`/admin/products/${editing}`, payload);
        toast.success("Product updated");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.slug}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-products-page" className="pt-28 pb-28">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-12 flex-wrap gap-4">
          <div>
            <Link to="/account" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-black">
              <ArrowLeft size={14} /> Account
            </Link>
            <h1 className="font-display text-4xl md:text-6xl tracking-[-0.03em] leading-none mt-3">Products</h1>
            <p className="text-sm text-muted-foreground mt-2">Manage the VYRON catalogue — add, edit, remove and upload images.</p>
          </div>
          {!editing && (
            <button onClick={openNew} data-testid="add-product-btn"
              className="inline-flex items-center gap-2 bg-black text-white rounded-full px-6 py-3 uppercase text-xs tracking-[0.2em] hover:bg-black/85 transition-colors">
              <Plus size={16} /> Add Product
            </button>
          )}
        </div>

        {/* Editor */}
        {editing && (
          <div className="rounded-3xl border border-black/10 bg-white p-6 md:p-10 mb-12" data-testid="product-editor">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-gold">{editing === "new" ? "New Product" : "Editing"}</p>
                <h2 className="font-display text-3xl mt-1">{form.name || "Untitled product"}</h2>
              </div>
              <div className="flex gap-2">
                <button onClick={cancel} className="rounded-full border border-black/20 px-5 py-2.5 text-xs uppercase tracking-[0.2em]">
                  <X size={14} className="inline mr-1.5" /> Cancel
                </button>
                <button onClick={save} disabled={busy} data-testid="save-product-btn"
                  className="rounded-full bg-black text-white px-6 py-2.5 text-xs uppercase tracking-[0.2em] disabled:opacity-60">
                  <Save size={14} className="inline mr-1.5" /> {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column */}
              <div className="space-y-5">
                <div>
                  <label className={label}>Name</label>
                  <input className={field} data-testid="product-name-input" value={form.name}
                    onChange={(e) => { setField("name", e.target.value); if (editing === "new") setField("slug", slugify(e.target.value)); }} />
                </div>
                <div>
                  <label className={label}>Slug (URL)</label>
                  <input className={field} data-testid="product-slug-input" value={form.slug}
                    onChange={(e) => setField("slug", slugify(e.target.value))} placeholder="vyron-noir-prestige" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={label}>SKU</label>
                    <input className={field} data-testid="product-sku-input" value={form.sku} onChange={(e) => setField("sku", e.target.value)} placeholder="VYR-NP-04" />
                  </div>
                  <div>
                    <label className={label}>Inventory</label>
                    <input className={field} data-testid="product-inventory-input" type="number" min={0} value={form.inventory} onChange={(e) => setField("inventory", Number(e.target.value))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={label}>Selling Price (₹)</label>
                    <input className={field} data-testid="product-price-input" type="number" min={0} value={form.price} onChange={(e) => setField("price", Number(e.target.value))} />
                  </div>
                  <div>
                    <label className={label}>Compare-at Price (₹)</label>
                    <input className={field} data-testid="product-mrp-input" type="number" min={0} value={form.compare_at_price || 0} onChange={(e) => setField("compare_at_price", Number(e.target.value) || null)} />
                  </div>
                </div>
                <div>
                  <label className={label}>Tagline</label>
                  <input className={field} value={form.tagline} onChange={(e) => setField("tagline", e.target.value)} placeholder="Signature Collection" />
                </div>
                <div>
                  <label className={label}>Description</label>
                  <textarea className={`${field} min-h-[120px]`} data-testid="product-description-input" value={form.description} onChange={(e) => setField("description", e.target.value)} />
                </div>
                <div>
                  <label className={label}>Collections</label>
                  <div className="flex flex-wrap gap-2">
                    {COLLECTION_OPTIONS.map((c) => {
                      const active = form.collections.includes(c.v);
                      return (
                        <button key={c.v} type="button" onClick={() =>
                          setField("collections", active ? form.collections.filter((x) => x !== c.v) : [...form.collections, c.v])
                        }
                        className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full border transition-colors ${active ? "bg-black text-white border-black" : "border-black/15 hover:border-black"}`}>{c.l}</button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center gap-3 text-sm">
                    <input type="checkbox" checked={form.new_arrival} onChange={(e) => setField("new_arrival", e.target.checked)} /> New Arrival
                  </label>
                  <label className="flex items-center gap-3 text-sm">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setField("featured", e.target.checked)} /> Featured
                  </label>
                  <label className="flex items-center gap-3 text-sm">
                    <input type="checkbox" checked={form.best_seller} onChange={(e) => setField("best_seller", e.target.checked)} /> Best Seller
                  </label>
                  <label className="flex items-center gap-3 text-sm">
                    <input type="checkbox" checked={form.active} onChange={(e) => setField("active", e.target.checked)} /> Published
                  </label>
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-5">
                <div>
                  <label className={label}>Product Images</label>
                  <div className="grid grid-cols-3 gap-3">
                    {form.images.map((url, i) => (
                      <div key={url + i} className="relative group aspect-square rounded-2xl overflow-hidden bg-[#F5F5F5]" data-testid={`image-tile-${i}`}>
                        <img src={url} alt="" className="w-full h-full object-contain" />
                        <button type="button" onClick={() => removeImage(i)}
                          className="absolute top-2 right-2 bg-white/95 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                    <label className="aspect-square rounded-2xl border-2 border-dashed border-black/15 flex flex-col items-center justify-center gap-1.5 cursor-pointer hover:border-black transition-colors">
                      <input type="file" accept="image/*" multiple className="hidden" onChange={onFilesSelected} data-testid="upload-image-input" />
                      {uploading ? <span className="text-xs">Uploading…</span> : <><Upload size={18} /><span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Upload</span></>}
                    </label>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2">JPEG, PNG or WEBP · max 10MB each · first image is the hero.</p>
                </div>
                <div>
                  <label className={label}>Features (one per line)</label>
                  <textarea className={`${field} min-h-[120px]`} value={form.features.join("\n")} onChange={(e) => setListField("features", e.target.value)} placeholder="UV400 Protection" />
                </div>
                <div>
                  <label className={label}>Specifications (Key: Value, one per line)</label>
                  <textarea className={`${field} min-h-[130px]`} value={Object.entries(form.specs).map(([k, v]) => `${k}: ${v}`).join("\n")} onChange={(e) => setSpecsField(e.target.value)} placeholder="Frame: Premium Acetate" />
                </div>
                <div>
                  <label className={label}>Colors (one per line)</label>
                  <textarea className={`${field} min-h-[60px]`} value={form.colors.join("\n")} onChange={(e) => setListField("colors", e.target.value)} placeholder="Gloss Black / Gold" />
                </div>
                <div>
                  <label className={label}>In the Box (one per line)</label>
                  <textarea className={`${field} min-h-[80px]`} value={form.in_the_box.join("\n")} onChange={(e) => setListField("in_the_box", e.target.value)} />
                </div>
                <div>
                  <label className={label}>Tags (one per line, for search)</label>
                  <textarea className={`${field} min-h-[80px]`} value={form.tags.join("\n")} onChange={(e) => setListField("tags", e.target.value)} placeholder="polarized" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Product list */}
        <div className="space-y-3" data-testid="product-list">
          {products.length === 0 && (
            <p className="text-sm text-muted-foreground py-12 text-center">No products yet. Click <b>Add Product</b> to create your first one.</p>
          )}
          {products.map((p) => (
            <div key={p.slug} data-testid={`product-row-${p.slug}`}
              className="grid grid-cols-[70px_1fr_auto] items-center gap-5 rounded-2xl bg-white border border-black/10 p-4">
              <div className="aspect-square rounded-xl overflow-hidden bg-[#F5F5F5]">
                {p.images[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-contain" loading="lazy" /> : <ImageIcon size={22} className="m-auto opacity-40" />}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{p.sku || "—"} · {p.slug}</p>
                <p className="font-display text-xl md:text-2xl leading-tight mt-1">{p.name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatINR(p.price)}{p.compare_at_price ? <span className="line-through ml-2">{formatINR(p.compare_at_price)}</span> : null}
                  <span className="mx-2">·</span> Inv <b>{p.inventory}</b>
                  <span className="mx-2">·</span> {p.collections.length ? p.collections.join(", ") : "no collections"}
                  {p.active === false && <span className="ml-2 text-orange-600">· hidden</span>}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`}
                  className="rounded-full border border-black/20 px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-black hover:text-white transition-colors">
                  <Edit3 size={12} className="inline mr-1.5" /> Edit
                </button>
                <button onClick={() => del(p)} data-testid={`delete-${p.slug}`}
                  className="rounded-full border border-red-200 px-4 py-2 text-xs uppercase tracking-[0.15em] text-red-600 hover:bg-red-50 transition-colors">
                  <Trash2 size={12} className="inline mr-1.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
